import { format, isValid } from 'date-fns';
import numeral from 'numeral';
import { utcToZonedTime } from 'date-fns-tz';
import { NIL as NIL_UUID } from 'uuid';

export const emptyGuid = NIL_UUID;
export const getFixed = (value: number | undefined, fraction: number): string => {
	const incomingValue = Number(value);

	return value && !isNaN(incomingValue)
		? incomingValue.toFixed(fraction)
		: (0).toFixed(fraction);
};

export const getFormattedUTCDate = (value: string, formatString: string,): string => {
	if (value) {
		const zonedDate = utcToZonedTime(value, 'UTC');
		return format(zonedDate, formatString);
	} else {
		return '-';
	}
};

export const getFormattedDate = (
	value: Date | string | undefined | number,
	formatString: string,
	defaultValue?: string | number | Date | undefined
): string => {
	const incomingValue = new Date(value);
	if (value && isValid(incomingValue)) {
		return format(incomingValue, formatString);
	} else if (defaultValue) {
		return defaultValue.toString();
	} else {
		return '-';
	}
};

declare const navigator: {
	clipboard: {
		writeText: (param: string) => Promise<void>;
	};
};

const fallbackCopyTextToClipboard = async (text: string): Promise<void> => {
	const textArea = document.createElement('textarea');
	textArea.value = text;
	document.body.appendChild(textArea);
	textArea.focus();
	textArea.select();

	try {
		document.execCommand('copy');
		document.body.removeChild(textArea);
		return Promise.resolve();
	} catch (err) {
		document.body.removeChild(textArea);
		return Promise.reject();
	}
};

export const copyTextToClipboard = (text: string): Promise<void> => {
	if (!navigator.clipboard) {
		return fallbackCopyTextToClipboard(text);
	}
	return navigator.clipboard.writeText(text);
};

export const makeSearchParams = (params): string => {
	const keys = Object.keys(params || {});
	let options = '';

	keys?.forEach((key) => {
		const isParamTypeObject = typeof params[key] === 'object';
		const isParamTypeArray = isParamTypeObject && (params[key]?.length >= 0);

		if (!isParamTypeObject && !!params[key]) {
			options += `${key}=${params[key]}&`;
		}

		if (isParamTypeObject && isParamTypeArray) {
			params[key]?.forEach((element) => {
				options += `${key}=${element}&`;
			});
		}
	});

	return options ? options.slice(0, -1) : options;
};

interface IObjectParams {
	[key: string]: string | string[] | Date;
}

export const parseSearchParams = (search: string): any => {
	if (!search) return undefined;

	const params: IObjectParams = {};
	search
		?.replace('?', '')
		?.split('&')
		?.forEach((param) => {
			const [key, value] = param.split('=');

			let parsedValue: any = decodeURIComponent(value)?.replace(/%20/g, ' '); // replace whitespaces to real spaces

			if (parsedValue?.toLowerCase() === 'true') {
				parsedValue = 'true';
			} else if ((parsedValue as string)?.toLowerCase() === 'false') {
				parsedValue = 'false';
			}

			if (params[key]) {
				if (Array.isArray(params[key])) {
					(params[key] as string[]).push(parsedValue);
				} else {
					params[key] = [params[key] as string, parsedValue];
				}
			} else {
				params[key] = parsedValue;
			}
	});

	return params;
};

export const removeNullValues = (obj: IObjectParams): IObjectParams => {
	return Object.fromEntries(
		Object.entries(obj).filter(([_, value]) => value !== 'null')
	);
};

export const numberFormatter = (value: number): string => {
	const formatter = new Intl.NumberFormat('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return formatter.format(value);
};

export const numberFormatterCount = (value: number): string => {
	if (value >= 0) {
		const formatter = new Intl.NumberFormat('en-US');
		return formatter.format(value);
	} else {
		return '-';
	}
};

export const valueFormatter = (value: number): string => {
	if (value < 1000) {
		return String(value);
	} else {
		return numeral(value).format('0.0a');
	}
};

export const getListItemValue = (key: string, list: any[]): string => {
	return list.find((item) => item.value === key)?.label;
};

export const getParsedJsonData = (data: string): string => {
	let json = '';
	if (data) {
		const parsedJSON = JSON.parse(data);
		json = JSON.stringify(parsedJSON, null, 4);
	}
	return json;
};

export const parseContentTypeToReadableFormat = (contentType: string): string => {
	const contentTypeMap: Record<string, string> = {
		'application/pdf': 'PDF Document',
		'application/msword': 'Word Document',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
		'application/vnd.ms-excel': 'Excel Spreadsheet',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
		'text/plain': 'Text File',
		'text/csv': 'CSV File',
		'image/jpeg': 'JPEG Image',
		'image/png': 'PNG Image',
	};

	return contentTypeMap[contentType] || '-';
};

export const isUSCountry = (countryValue: string): boolean => {
	if (!countryValue) return false;
	const usValues = ['us', 'usa', 'united states', 'united states of america'];
	return usValues.includes(countryValue.toLowerCase());
};
